import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiGlobe } from 'react-icons/fi';
import styles from './LanguageSwitcher.module.css';
import { basenameFor, stripLangPrefix } from '../../utils/locale';
import { useShowHebrew } from '../../hooks/useShowHebrew';
import { filterByHebrewFlag } from '../../utils/languageGating';

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'he', label: 'עב' },
  { code: 'ar', label: 'ع' },
];

const getCurrentLabel = (language) => {
  const match = LANGUAGES.find(({ code }) => language === code || language.startsWith(code));
  return match ? match.label : 'EN';
};

/**
 * Switch to a new locale by hard-navigating to the same logical path under the new
 * language prefix. Hard navigation (not client-side push) is intentional: it lets
 * BrowserRouter's basename re-read from the URL at mount, keeping every internal
 * Link consistent with the active language. Also persists choice in localStorage so
 * a user who lands on / next time still gets their language via the inline detector
 * in public/index.html (used only as a fallback, since URL is canonical).
 */
function switchLocale(targetLang) {
  try { localStorage.setItem('stylon_company_lang', targetLang); } catch (_) {}
  const logical = stripLangPrefix(window.location.pathname) || '/';
  const newPrefix = basenameFor(targetLang);
  const search = window.location.search || '';
  const hash = window.location.hash || '';
  const target = `${newPrefix}${logical === '/' ? '/' : logical}${search}${hash}`;
  window.location.assign(target);
}

const LanguageSwitcher = ({ variant = 'dark' }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  // Hebrew is Israel-only on the marketing site (by IP).
  const showHebrew = useShowHebrew();
  const languages = filterByHebrewFlag(LANGUAGES, showHebrew, (l) => l.code);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Inline variant for drawer — no popup, just pill buttons side by side
  if (variant === 'drawer') {
    return (
      <div className={styles.inlineRow}>
        {languages.map(({ code, label }) => {
          const isActive = i18n.language === code || i18n.language.startsWith(code);
          return (
            <button
              key={code}
              type="button"
              className={`${styles.inlinePill} ${isActive ? styles.inlinePillActive : ''}`}
              onClick={() => switchLocale(code)}
            >
              {label}
            </button>
          );
        })}
      </div>
    );
  }

  const currentLabel = getCurrentLabel(i18n.language);

  return (
    <div className={styles.wrapper} ref={ref}>
      <button
        type="button"
        className={`${styles.triggerBtn} ${variant === 'light' ? styles.triggerBtnLight : ''}`}
        onClick={() => setOpen(!open)}
        aria-label="Select language"
        aria-expanded={open}
      >
        <FiGlobe className={styles.triggerIcon} />
        <span className={styles.triggerLabel}>{currentLabel}</span>
      </button>
      {open && (
        <div className={styles.dropdown}>
          {languages.map(({ code, label }) => (
            <button
              key={code}
              type="button"
              className={`${styles.dropdownItem} ${i18n.language === code || i18n.language.startsWith(code) ? styles.dropdownItemActive : ''}`}
              onClick={() => {
                setOpen(false);
                switchLocale(code);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
